import React, { useState, useEffect, useRef } from 'react';
import { X, Upload, Image, AlertCircle, ChevronDown, Trash2 } from 'lucide-react';
import type { LegalFile } from '../../types';
import { useSystemPreset } from '../../contexts/SystemPresetContext';

interface AddAttachmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedFile: LegalFile;
  onAddAttachment: (attachmentData: {
    attachmentURL: string;
    caseID: string;
    createdAt: string;
    createdBy: string;
    isArchived: boolean;
    isDeleted: boolean;
    lawyerID: string;
    notes: string;
    type: string;
    updatedAt: string;
    updatedBy: string;
    format: string;
  }) => Promise<void>;
  user: any;
  selectedRole: any;
  language: string;
  strings: any;
}

const AddAttachmentModal: React.FC<AddAttachmentModalProps> = ({
  isOpen,
  onClose,
  selectedFile,
  onAddAttachment,
  user,
  selectedRole,
  language,
  strings
}) => {
  const { presetData } = useSystemPreset();
  const [selectedFileForUpload, setSelectedFileForUpload] = useState<File | null>(null);
  const [selectedAttachmentType, setSelectedAttachmentType] = useState<string>('');
  const [attachmentNotes, setAttachmentNotes] = useState<string>('');
  const [imagePreview, setImagePreview] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Supported file types and their extensions
  const supportedFileTypes = {
    images: ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'svg'],
    documents: ['pdf', 'doc', 'docx'],
    spreadsheets: ['xls', 'xlsx', 'csv'],
    presentations: ['ppt', 'pptx'],
    text: ['txt', 'rtf']
  };

  // Get file extension from filename
  const getFileExtension = (filename: string): string => {
    return filename.split('.').pop()?.toLowerCase() || '';
  };

  // Get file type category from extension
  const getFileTypeCategory = (extension: string): string => {
    if (supportedFileTypes.images.includes(extension)) return 'image';
    if (supportedFileTypes.documents.includes(extension)) return 'document';
    if (supportedFileTypes.spreadsheets.includes(extension)) return 'spreadsheet';
    if (supportedFileTypes.presentations.includes(extension)) return 'presentation';
    if (supportedFileTypes.text.includes(extension)) return 'text';
    return 'other';
  };

  // Get file icon based on extension
  const getFileIcon = (extension: string): string => {
    const category = getFileTypeCategory(extension);
    switch (category) {
      case 'image': return '🖼️';
      case 'document': return '📄';
      case 'spreadsheet': return '📊';
      case 'presentation': return '📽️';
      case 'text': return '📝';
      default: return '📎';
    }
  };

  // Get MIME type from extension
  const getMimeTypeFromExtension = (extension: string): string => {
    const mimeTypes: { [key: string]: string } = {
      'png': 'image/png',
      'jpg': 'image/jpeg',
      'jpeg': 'image/jpeg',
      'gif': 'image/gif',
      'bmp': 'image/bmp',
      'webp': 'image/webp',
      'svg': 'image/svg+xml',
      'pdf': 'application/pdf',
      'doc': 'application/msword',
      'docx': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'xls': 'application/vnd.ms-excel',
      'xlsx': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'csv': 'text/csv',
      'ppt': 'application/vnd.ms-powerpoint',
      'pptx': 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      'txt': 'text/plain',
      'rtf': 'application/rtf'
    };
    return mimeTypes[extension] || 'application/octet-stream';
  };

  // Function to encode file to base64
  const encodeFileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Extract base64 data without data URL prefix
        const base64Data = result.split(',')[1];
        resolve(base64Data);
      };
      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };
      reader.readAsDataURL(file);
    });
  };

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const extension = getFileExtension(file.name);
      
      // Validate file type
      const allSupportedExtensions = [
        ...supportedFileTypes.images,
        ...supportedFileTypes.documents,
        ...supportedFileTypes.spreadsheets,
        ...supportedFileTypes.presentations,
        ...supportedFileTypes.text
      ];
      
      if (!allSupportedExtensions.includes(extension)) {
        setError(language === 'ar' 
          ? 'نوع الملف غير مدعوم. الأنواع المدعومة: الصور، PDF، Word، Excel، PowerPoint، النصوص' 
          : 'File type not supported. Supported types: Images, PDF, Word, Excel, PowerPoint, Text');
        return;
      }
      
      // Validate file size (max 5MB for free tier)
      if (file.size > 5 * 1024 * 1024) {
        setError(language === 'ar' 
          ? 'حجم الملف يجب أن يكون أقل من 5 ميجابايت' 
          : 'File size must be less than 5MB');
        return;
      }
      
      setSelectedFileForUpload(file);
      
      // Auto-detect file type based on extension
      const category = getFileTypeCategory(extension);
      let detectedType = '1'; // Default to image
      
      if (category === 'document') detectedType = '2';
      else if (category === 'spreadsheet') detectedType = '3';
      else if (category === 'presentation') detectedType = '4';
      else if (category === 'text') detectedType = '5';
      else if (category === 'image') detectedType = '1';
      
      setSelectedAttachmentType(detectedType);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
      
      setError('');
    }
  };

  const handleSubmit = async () => {
    console.log('🎯 AddAttachmentModal handleSubmit called');
    console.log('📁 selectedFileForUpload:', selectedFileForUpload);
    console.log('🏷️ selectedAttachmentType:', selectedAttachmentType);
    console.log('📝 attachmentNotes:', attachmentNotes);
    console.log('👤 user:', user);
    console.log('🎭 selectedRole:', selectedRole);

    if (!selectedFileForUpload) {
      setError(language === 'ar' ? 'يرجى اختيار ملف للرفع' : 'Please select a file to upload');
      return;
    }

    if (!selectedAttachmentType) {
      setError(language === 'ar' ? 'يرجى اختيار نوع المرفق' : 'Please select attachment type');
      return;
    }

    if (!user || !selectedRole) {
      setError(language === 'ar' ? 'معلومات المستخدم غير مكتملة' : 'User information incomplete');
      return;
    }

    try {
      setIsUploading(true);
      setError('');

      console.log('📤 Starting file upload...');
      
      // Encode file to base64
      const base64Data = await encodeFileToBase64(selectedFileForUpload);
      console.log('✅ File encoded to base64, length:', base64Data.length);
      
      // Get file extension
      const fileExtension = getFileExtension(selectedFileForUpload.name);
      console.log('📎 File extension:', fileExtension);
      
      // Get MIME type
      const mimeType = getMimeTypeFromExtension(fileExtension);
      console.log('🎭 MIME type:', mimeType);

      const attachmentData = {
        attachmentURL: base64Data,
        caseID: selectedFile.id,
        createdAt: Date.now().toString(),
        createdBy: user.id,
        isArchived: false,
        isDeleted: false,
        lawyerID: selectedRole.lawyerID || user.id,
        notes: attachmentNotes || selectedFileForUpload.name,
        type: selectedAttachmentType,
        updatedAt: Date.now().toString(),
        updatedBy: user.id,
        format: fileExtension // Add the format field
      };

      console.log('📦 Attachment data prepared:', attachmentData);
      
      await onAddAttachment(attachmentData);
      
      console.log('✅ Attachment added successfully');
      
      // Reset form
      setSelectedFileForUpload(null);
      setSelectedAttachmentType('');
      setAttachmentNotes('');
      setImagePreview('');
      setError('');
      
      // Close modal
      onClose();
      
    } catch (error) {
      console.error('❌ Error adding attachment:', error);
      setError(language === 'ar' ? 'حدث خطأ أثناء رفع المرفق' : 'Error uploading attachment');
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveFile = () => {
    setSelectedFileForUpload(null);
    setImagePreview('');
    setError('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) {
      // Create a mock FileList object
      const mockFileList = {
        0: file,
        length: 1,
        item: (index: number) => index === 0 ? file : null,
        [Symbol.iterator]: function* () {
          yield file;
        }
      } as FileList;
      
      const event = {
        target: { 
          files: mockFileList
        }
      } as unknown as React.ChangeEvent<HTMLInputElement>;
      handleFileSelect(event);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (showTypeDropdown) {
        setShowTypeDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTypeDropdown]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Modal Header */}
        <div className="p-6 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Image className="w-5 h-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-800 dark:text-white">
                  {language === 'ar' ? 'إضافة مرفق' : 'Add Attachment'}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">
                  {selectedFile.title}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {/* Error Message */}
            {error && (
              <div className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-600 dark:text-red-400">
                <div className="flex items-center space-x-2">
                  <AlertCircle className="w-4 h-4" />
                  <span>{error}</span>
                </div>
              </div>
            )}

            {/* File Upload Area */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                {language === 'ar' ? 'اختيار الملف' : 'Select File'}
              </label>
              <div 
                className="relative"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <input
                  ref={fileInputRef}
                  type="file"
                  onChange={handleFileSelect}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  accept=".png,.jpg,.jpeg,.gif,.bmp,.webp,.svg,.pdf,.doc,.docx,.xls,.xlsx,.csv,.ppt,.pptx,.txt,.rtf"
                />
                <div className="border-2 border-dashed border-slate-300 dark:border-slate-600 rounded-lg p-6 text-center hover:border-primary transition-colors duration-200">
                  {selectedFileForUpload ? (
                    <div className="space-y-3">
                      {/* File Info */}
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                          <span className="text-2xl">
                            {getFileIcon(getFileExtension(selectedFileForUpload.name))}
                          </span>
                        </div>
                        <div className="flex-1 text-left">
                          <p className="font-medium text-slate-800 dark:text-white truncate">
                            {selectedFileForUpload.name}
                          </p>
                          <p className="text-sm text-slate-500 dark:text-slate-400">
                            {(selectedFileForUpload.size / 1024 / 1024).toFixed(2)} MB • {getFileExtension(selectedFileForUpload.name).toUpperCase()}
                          </p>
                        </div>
                        <button
                          onClick={handleRemoveFile}
                          className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </button>
                      </div>
                      
                      {/* Image Preview (only for images) */}
                      {getFileTypeCategory(getFileExtension(selectedFileForUpload.name)) === 'image' && imagePreview && (
                        <div className="w-full h-32 bg-slate-100 dark:bg-slate-700 rounded-lg overflow-hidden">
                          <img 
                            src={imagePreview} 
                            alt="Preview" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Upload className="w-8 h-8 text-slate-400 mx-auto" />
                      <div>
                        <p className="text-sm font-medium text-slate-600 dark:text-slate-400">
                          {language === 'ar' ? 'اسحب الملف هنا أو انقر للاختيار' : 'Drag file here or click to select'}
                        </p>
                        <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                          {language === 'ar' 
                            ? 'الصور، PDF، Word، Excel، PowerPoint، النصوص (حد أقصى 5 ميجابايت)' 
                            : 'Images, PDF, Word, Excel, PowerPoint, Text (max 5MB)'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Attachment Type */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                {language === 'ar' ? 'نوع المرفق' : 'Attachment Type'}
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowTypeDropdown(!showTypeDropdown)}
                  className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg hover:border-primary transition-colors"
                >
                  <span className="text-slate-700 dark:text-white">
                    {selectedAttachmentType 
                      ? (presetData?.attachmentTypes?.find((type: any) => type.id === selectedAttachmentType)?.[language === 'ar' ? 'name_ar' : 'name_en'] || 'Select Type')
                      : (language === 'ar' ? 'اختر النوع' : 'Select Type')
                    }
                  </span>
                  <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${showTypeDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showTypeDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white dark:bg-slate-700 border border-slate-300 dark:border-slate-600 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {presetData?.attachmentTypes?.map((type: any) => (
                      <button
                        key={type.id}
                        onClick={() => {
                          setSelectedAttachmentType(type.id);
                          setShowTypeDropdown(false);
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors first:rounded-t-lg last:rounded-b-lg"
                      >
                        <div className="flex items-center space-x-3">
                          <span className="text-lg">{type.icon}</span>
                          <span className="text-slate-700 dark:text-white">{type[language === 'ar' ? 'name_ar' : 'name_en']}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-3">
                {language === 'ar' ? 'ملاحظات (اختياري)' : 'Notes (Optional)'}
              </label>
              <textarea
                value={attachmentNotes}
                onChange={(e) => setAttachmentNotes(e.target.value)}
                placeholder={language === 'ar' ? 'أضف ملاحظات حول المرفق...' : 'Add notes about the attachment...'}
                className="w-full px-4 py-3 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent dark:bg-slate-700 dark:text-white resize-none"
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="p-6 border-t border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center justify-end space-x-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-slate-600 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 transition-colors"
            >
              {language === 'ar' ? 'إلغاء' : 'Cancel'}
            </button>
            <button
              onClick={handleSubmit}
              disabled={!selectedFileForUpload || !selectedAttachmentType || isUploading}
              className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center space-x-2"
            >
              {isUploading && (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              )}
              <span>
                {isUploading 
                  ? (language === 'ar' ? 'جاري الرفع...' : 'Uploading...') 
                  : (language === 'ar' ? 'إضافة المرفق' : 'Add Attachment')
                }
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddAttachmentModal;